import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { jwtDecode } from "jwt-decode";
import { rideApi } from "../../apis/rideApi";
import { partnerDashboardService } from "../../apis/partnerDashboard";
import { Ride, RideStatus, RideType } from "../../types/ride";
import SidePanel from "../../components/SidePanel";
import TopBar from "../../components/TopBar";

const getStatusColor = (status: RideStatus) => {
  const colors: Record<
    RideStatus,
    "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
  > = {
    [RideStatus.PENDING]: "warning",
    [RideStatus.ACCEPTED]: "info",
    [RideStatus.STARTED]: "primary",
    [RideStatus.ARRIVED]: "info",
    [RideStatus.ENDED]: "success",
    [RideStatus.CANCELED]: "error",
    [RideStatus.PAYMENT_SUCCEED]: "success",
    [RideStatus.PAYMENT_FAILED]: "error",
    [RideStatus.CANCELED_WITH_PAYMENT]: "success",
  };
  return colors[status];
};

const getRideTypeLabel = (type: RideType) => {
  const labels: Record<RideType, string> = {
    [RideType.NORMAL]: "Normal",
    [RideType.SHARED]: "Shared",
    [RideType.DELIVERY]: "Delivery",
  };
  return labels[type];
};

export default function RideDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ride, setRide] = useState<Ride | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPartner, setIsPartner] = useState(false);

  useEffect(() => {
    const fetchRideDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        
        // Check if user is partner or partner-agent
        const token = localStorage.getItem("token");
        let data: Ride;
        
        if (token) {
          const decoded: any = jwtDecode(token);
          const userRole = decoded.role;
          
          // If user is partner or partner-agent, use partner endpoint
          if (userRole === "partner" || userRole === "partner-agent") {
            setIsPartner(true);
            const partnerId = userRole === "partner-agent" ? decoded.partnerId : decoded._id;
            data = await partnerDashboardService.getPartnerRideDetails(partnerId, id);
          } else {
            // For admin/other roles, use admin endpoint
            setIsPartner(false);
            data = await rideApi.getRideDetails(id);
          }
        } else {
          // Fallback to admin endpoint if no token
          setIsPartner(false);
          data = await rideApi.getRideDetails(id);
        }
        
        setRide(data);
      } catch (err) {
        setError("Failed to load ride details. Please try again later.");
        console.error("Error fetching ride details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRideDetails();
  }, [id]);

  if (loading) {
    return (
      <Box className="mainContainer">
        <Box className="sidePanelContainer">
          <SidePanel isOpen={true} onClose={() => {}} />
        </Box>
        <Box className="dashboardContainer">
          <TopBar />
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error || !ride) {
    return (
      <Box className="mainContainer">
        <Box className="sidePanelContainer">
          <SidePanel isOpen={true} onClose={() => {}} />
        </Box>
        <Box className="dashboardContainer">
          <TopBar />
          <Box sx={{ width: "100%", padding: "20px", marginTop: "5%" }}>
            <Alert severity="error" sx={{ mt: 3 }}>
              {error || "Ride not found"}
            </Alert>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="mainContainer">
      <Box className="sidePanelContainer">
        <SidePanel isOpen={true} onClose={() => {}} />
      </Box>
      <Box className="dashboardContainer">
        <TopBar />
        <Box sx={{ width: "100%", padding: "20px", marginTop: "5%" }}>
          <Paper elevation={3} sx={{ padding: "20px" }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/rides")}
                variant="outlined"
              >
                Back to Rides
              </Button>
              <Box>
                <Chip
                  label={getRideTypeLabel(ride.ride_type)}
                  color={ride.ride_type === RideType.NORMAL ? "primary" : "secondary"}
                  sx={{ mr: 1, fontWeight: 500 }}
                />
                <Chip
                  label={ride.ride_status}
                  color={getStatusColor(ride.ride_status)}
                  sx={
                    ride.ride_status === RideStatus.CANCELED_WITH_PAYMENT
                      ? { backgroundColor: "#CBE256", color: "#222" }
                      : { fontWeight: 500 }
                  }
                />
              </Box>
            </Box>

            <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
              Ride Details
            </Typography>

            <Grid container spacing={3}>
              {!isPartner && (
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Customer Information
                    </Typography>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {ride.rider_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Phone: {ride.rider_phone_number}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Email: {ride.rider_email}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12} md={isPartner ? 12 : 6}>
                <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Driver Information
                  </Typography>
                  {ride.driver ? (
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {ride.driver.driver_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Phone: {ride.driver.driver_phone_number}
                      </Typography>
                      {ride.driver.driver_email && (
                        <Typography variant="body2" color="text.secondary">
                          Email: {ride.driver.driver_email}
                        </Typography>
                      )}
                      {ride.driver.driver_picture && (
                        <Box sx={{ mt: 2 }}>
                          <img
                            src={ride.driver.driver_picture}
                            alt={ride.driver.driver_name}
                            style={{
                              width: 100,
                              height: 100,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      )}
                      {ride.car && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Vehicle Details
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {ride.car.make} {ride.car.model}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Registration: {ride.car.car_registration_number}
                          </Typography>
                          {ride.car.car_type && (
                            <Typography variant="body2" color="text.secondary">
                              Type: {ride.car.car_type}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No driver assigned
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Trip Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Pickup Location
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {ride.pickup_location.place}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ride.pickup_location.city && `${ride.pickup_location.city}, `}
                          {ride.pickup_location.region_code &&
                            `${ride.pickup_location.region_code}, `}
                          {ride.pickup_location.country_code}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Dropoff Location
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {ride.destination_location.place}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ride.destination_location.city && `${ride.destination_location.city}, `}
                          {ride.destination_location.region_code &&
                            `${ride.destination_location.region_code}, `}
                          {ride.destination_location.country_code}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Trip Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Fare
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {Math.round(ride.fare_amount).toLocaleString()} FCFA
                        </Typography>
                        {ride.payment_mode && (
                          <Typography variant="caption" color="text.secondary">
                            Payment: {ride.payment_mode}
                          </Typography>
                        )}
                        {typeof ride.driver_payable_amount === "number" &&
                          ride.driver_payable_amount > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Driver: {Math.round(ride.driver_payable_amount).toLocaleString()} FCFA
                            </Typography>
                          )}
                        {typeof ride.teracar_commission === "number" &&
                          ride.teracar_commission > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              TeraCar: {Math.round(ride.teracar_commission).toLocaleString()} FCFA
                            </Typography>
                          )}
                        {typeof ride.partner_commission === "number" &&
                          ride.partner_commission > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Partner: {Math.round(ride.partner_commission).toLocaleString()} FCFA
                            </Typography>
                          )}
                        {typeof ride.discount_amount === "number" && ride.discount_amount > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Discount: {Math.round(ride.discount_amount).toLocaleString()} FCFA
                          </Typography>
                        )}
                        {typeof ride.premium_fee === "number" && ride.premium_fee > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Premium Fee: {Math.round(ride.premium_fee).toLocaleString()} FCFA
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Status
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {ride.ride_status}
                        </Typography>
                        {ride.cancelled_by && ride.ride_status === RideStatus.CANCELED && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Cancelled by {ride.cancelled_by.toLowerCase()}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    {ride.eta && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            ETA
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {ride.eta}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {ride.car_type && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Car Type
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {ride.car_type}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {ride.ride_type === RideType.DELIVERY && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Delivery Details
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Package Details
                          </Typography>
                          {ride.package_details?.map((pkg, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                Type: {pkg.package_type}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Size: {pkg.package_size}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Weight: {pkg.weight} kg
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Dimensions: {pkg.length} x {pkg.width} x {pkg.height} cm
                              </Typography>
                              {pkg.package_description && (
                                <Typography variant="body2" color="text.secondary">
                                  Description: {pkg.package_description}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Recipient Information
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {ride.package_recipient?.full_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Contact: {ride.package_recipient?.contact_number}
                          </Typography>
                          {ride.package_recipient?.instructions && (
                            <Typography variant="body2" color="text.secondary">
                              Instructions: {ride.package_recipient.instructions}
                            </Typography>
                          )}
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Helper Required
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {ride.is_helper_needed ? "Yes" : "No"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Timeline
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Created At
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {format(new Date(ride.created_at), "MMM d, yyyy HH:mm")}
                        </Typography>
                      </Box>
                    </Grid>
                    {ride.start_time && (
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Started At
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {format(new Date(ride.start_time), "MMM d, yyyy HH:mm")}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {ride.end_time && (
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Ended At
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {format(new Date(ride.end_time), "MMM d, yyyy HH:mm")}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
